import type { TFunction } from "i18next";
import { List, Map } from "immutable";
import type {
  DepositAddressDto,
  DepositCheckDto,
  DepositCheckErrorData,
  DepositDto,
  DepositWalletsDto,
} from "types/api/deposits";
import api from "./api";
import { abortReason, isTransientError, sleep } from "./polling";

// While a payment check runs the backend scans every minute; otherwise every
// 5 min for an hour after the address was requested. No push notifications.
export const DEPOSIT_CHECK_POLL_MS = 10_000;
export const DEPOSIT_IDLE_POLL_MS = 30_000;
export const DEPOSIT_REQUEST_TIMEOUT_MS = 8_000;
// The backend refuses a second check within a minute of the last one.
const CHECK_COOLDOWN_MS = 60_000;

type DepositsApi = typeof api.deposits;

// client.js wraps every success as fromJS({ headers, data }).
const readData = <T>(response: unknown): T =>
  (response as Map<string, any>).get("data").toJS();

const readDeposits = (response: unknown): DepositDto[] =>
  (response as Map<string, any>).get("data", List()).toJS();

// The commission is two settings the backend adds up
// (DepositService::Process: amount × percent / 100 + fixed). Each arrives as
// a string ("10", "2.5"), a bare number from Setting.defaults, or null; older
// backends omit the fixed part, which must read as "no fixed fee".
export type DepositFee = {
  // Percent of the gross deposit, 0 ≤ percent < 100.
  percent: number;
  // Flat amount per deposit in the account currency (1:1 with USDT).
  fixed: number;
};

// A 100 % commission would leave nothing to credit and break the gross math
// (division by 100 − percent); the real setting is a small percent.
const MAX_COMMISSION_PERCENT = 99;

const parseSetting = (value: string | number | null | undefined): number => {
  const parsed = typeof value === "number" ? value : parseFloat(value ?? "");
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

export const parseDepositFee = (
  wallets:
    | Partial<
        Pick<
          DepositWalletsDto,
          "deposit_commission" | "deposit_commission_fixed"
        >
      >
    | null
    | undefined,
): DepositFee => ({
  percent: Math.min(
    parseSetting(wallets?.deposit_commission),
    MAX_COMMISSION_PERCENT,
  ),
  fixed: parseSetting(wallets?.deposit_commission_fixed),
});

// The address response carries the minimum the scanner applies; /wallets has
// the same setting as a fallback.
export const parseMinAmount = (
  address: Pick<DepositAddressDto, "min_amount"> | null | undefined,
  wallets?: Pick<DepositWalletsDto, "deposit_min_amount"> | null,
): number =>
  parseSetting(address?.min_amount) ||
  parseSetting(wallets?.deposit_min_amount);

// All money math runs in integer cents: amounts have two decimals on the
// backend (decimal(8,2)) and the fee is rounded there independently.
const toCents = (amount: number) => Math.round(amount * 100);
const fromCents = (cents: number) => cents / 100;

// What the backend debits for a gross deposit: the percent share rounded up
// plus the flat part.
const feeCentsForGross = (grossCents: number, fee: DepositFee) =>
  Math.ceil((grossCents * fee.percent) / 100) + toCents(fee.fixed);

const netCentsForGross = (grossCents: number, fee: DepositFee) =>
  Math.max(grossCents - feeCentsForGross(grossCents, fee), 0);

// Smallest gross deposit whose net credit (gross − fee) still covers `net`.
export const grossForNetCredit = (net: number, fee: DepositFee): number => {
  const netCents = toCents(net);
  if (netCents <= 0) return 0;

  let grossCents = Math.ceil(
    ((netCents + toCents(fee.fixed)) * 100) / (100 - fee.percent),
  );
  while (grossCents - feeCentsForGross(grossCents, fee) < netCents) {
    grossCents += 1;
  }
  return fromCents(grossCents);
};

// What to tell the user to send for `net`: transfers below the minimum are
// not credited automatically, so never ask for less.
export const sendAtLeast = (
  net: number,
  fee: DepositFee,
  minAmount: number,
): number => {
  const gross = grossForNetCredit(net, fee);
  return gross > 0 ? Math.max(gross, minAmount) : 0;
};

export const feeForGross = (gross: number, fee: DepositFee): number =>
  fromCents(feeCentsForGross(toCents(gross), fee));

export const netForGross = (gross: number, fee: DepositFee): number =>
  fromCents(netCentsForGross(toCents(gross), fee));

// What a set of deposits added to the balance: each transfer is charged the
// commission separately, so the fee applies per deposit.
export const creditedNet = (
  deposits: readonly DepositDto[],
  fee: DepositFee,
): number =>
  fromCents(
    deposits.reduce(
      (sum, deposit) =>
        sum + netCentsForGross(toCents(parseFloat(deposit.amount) || 0), fee),
      0,
    ),
  );

export const formatUsdt = (amount: number): string => amount.toFixed(2);

// Fee row value: "10%", "2.00 USDT", "10% + 2.00 USDT" or "0.00 USDT".
export const formatDepositFee = (fee: DepositFee): string => {
  const parts = [
    fee.percent > 0 ? `${fee.percent}%` : null,
    fee.fixed > 0 ? `${formatUsdt(fee.fixed)} USDT` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" + ") : "0.00 USDT";
};

// Fee row label: names whichever parts of the commission apply.
export const describeDepositFee = (t: TFunction, fee: DepositFee): string => {
  if (fee.percent > 0 && fee.fixed > 0) {
    return t("usdtDeposit.feeCombined", {
      percent: fee.percent,
      fixed: formatUsdt(fee.fixed),
    });
  }
  if (fee.percent > 0) {
    return t("usdtDeposit.feePercent", { percent: fee.percent });
  }
  return t("usdtDeposit.fee");
};

export const tronscanTxUrl = (txId: string): string =>
  `https://tronscan.org/#/transaction/${encodeURIComponent(txId)}`;

type RequestOptions = {
  signal?: AbortSignal;
  timeout?: number;
  depositsApi?: DepositsApi;
};

export const fetchDepositWallets = async (
  depositsApi: DepositsApi = api.deposits,
): Promise<DepositWalletsDto> => readData(await depositsApi.wallets());

export const fetchDepositAddress = async (
  depositsApi: DepositsApi = api.deposits,
): Promise<DepositAddressDto> => readData(await depositsApi.address());

// Credited deposits only, newest first.
export const fetchDeposits = async ({
  signal,
  timeout,
  depositsApi = api.deposits,
}: RequestOptions = {}): Promise<DepositDto[]> =>
  readDeposits(await depositsApi.list({ signal, timeout }));

// `checkUntil` only when a check started (202); `retryAt` is when the next
// one is allowed either way.
export type PaymentCheckOutcome = {
  checkUntil?: string;
  retryAt: string;
};

const cooldownEnd = () =>
  new Date(Date.now() + CHECK_COOLDOWN_MS).toISOString();

// client.js rejects plain { data, message, status } objects.
const readThrottle = (error: unknown): string | null => {
  const { status, data } = (error ?? {}) as {
    status?: unknown;
    data?: DepositCheckErrorData | null;
  };
  if (status !== 429) return null;
  return data?.retry_at ?? cooldownEnd();
};

// Asks the backend to scan the address now; the result shows up in the index.
export const requestPaymentCheck = async (
  depositsApi: DepositsApi = api.deposits,
): Promise<PaymentCheckOutcome> => {
  try {
    const { check_until } = readData<DepositCheckDto>(
      await depositsApi.check(),
    );
    return { checkUntil: check_until, retryAt: cooldownEnd() };
  } catch (error) {
    const retryAt = readThrottle(error);
    if (retryAt) return { retryAt };
    throw error;
  }
};

type WatchOptions = {
  signal: AbortSignal;
  intervalMs: number;
  onList: (deposits: DepositDto[]) => void;
  depositsApi?: DepositsApi;
};

// Polls the credited list until aborted, skipping polls while the tab is
// hidden. Transient errors are retried; any other error ends the loop.
// Rejects with an AbortError when the signal fires.
export const watchDeposits = async ({
  signal,
  intervalMs,
  onList,
  depositsApi = api.deposits,
}: WatchOptions): Promise<never> => {
  for (;;) {
    await sleep(intervalMs, signal);
    if (document.visibilityState !== "visible") continue;
    try {
      const deposits = await fetchDeposits({
        signal,
        timeout: DEPOSIT_REQUEST_TIMEOUT_MS,
        depositsApi,
      });
      if (signal.aborted) throw abortReason(signal);
      onList(deposits);
    } catch (error) {
      // client.js reports axios' cancellation as a plain network error.
      if (signal.aborted) throw abortReason(signal);
      if (!isTransientError(error)) throw error;
    }
  }
};
